import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NavigationBreadcrumbs from '../../components/ui/NavigationBreadcrumbs';
import MessageCard from './components/MessageCard';
import MessageFilters from './components/MessageFilters';
import MessageDetailsModal from './components/MessageDetailsModal';
import MessageAnalytics from './components/MessageAnalytics';
import ExportModal from './components/ExportModal';

const MessageHistory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // Mock message data
  const mockMessages = [
    {
      id: 1,
      recipient: {
        id: 1,
        name: "Sarah Johnson",
        email: "sarah.johnson@email.com",
        phone: "+1 (555) 123-4567",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
      },
      content: "Happy Birthday Sarah! 🎉 Hope you have a wonderful day filled with joy and celebration. Wishing you all the best for the year ahead!",
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      status: "delivered",
      channel: "email",
      type: "birthday",
      deliveryDetails: {
        attempts: 1,
        lastAttempt: new Date(Date.now() - 2 * 60 * 60 * 1000)
      },
      response: {
        content: "Thank you so much! This made my day! 😊",
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000)
      }
    },
    {
      id: 2,
      recipient: {
        id: 2,
        name: "Michael Chen",
        email: "michael.chen@company.com",
        phone: "+1 (555) 234-5678",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face"
      },
      content: "Hi Michael! It's been a while since we last connected. How have you been? Would love to catch up soon!",
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000), // 5 hours ago
      status: "pending",
      channel: "sms",
      type: "random",
      deliveryDetails: {
        attempts: 2,
        lastAttempt: new Date(Date.now() - 30 * 60 * 1000)
      }
    },
    {
      id: 3,
      recipient: {
        id: 3,
        name: "Emily Rodriguez",
        email: "emily.rodriguez@email.com",
        phone: "+1 (555) 345-6789",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face"
      },
      content: "Thanks for the great meeting today! Looking forward to working together on the upcoming project.",
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      status: "delivered",
      channel: "whatsapp",
      type: "manual",
      deliveryDetails: {
        attempts: 1,
        lastAttempt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
      }
    },
    {
      id: 4,
      recipient: {
        id: 4,
        name: "David Thompson",
        email: "david.thompson@email.com",
        phone: "+1 (555) 456-7890"
      },
      content: "Happy Birthday David! 🎂 May this new year of life bring you happiness, success, and all your heart desires!",
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      status: "failed",
      channel: "email",
      type: "birthday",
      deliveryDetails: {
        attempts: 3,
        lastAttempt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        error: "Email address not found. Please verify the recipient's email address."
      }
    },
    {
      id: 5,
      recipient: {
        id: 5,
        name: "Lisa Wang",
        email: "lisa.wang@startup.com",
        phone: "+1 (555) 567-8901",
        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face"
      },
      content: "Hey Lisa! Hope you're doing well. Just wanted to check in and see how the new startup is going!",
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      status: "delivered",
      channel: "sms",
      type: "random",
      deliveryDetails: {
        attempts: 1,
        lastAttempt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
      },
      response: {
        content: "Hi! Thanks for reaching out. Things are going great, would love to tell you more over coffee!",
        timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000 + 2 * 60 * 60 * 1000)
      }
    }
  ];

  // Mock analytics data
  const mockAnalytics = {
    totalMessages: 1247,
    deliveredMessages: 1089,
    failedMessages: 73,
    pendingMessages: 85,
    successRate: 87.3,
    channelBreakdown: [
      { channel: 'email', count: 687, successRate: 89 },
      { channel: 'sms', count: 342, successRate: 94 },
      { channel: 'whatsapp', count: 218, successRate: 82 }
    ],
    monthlyTrends: [
      { month: 'Jan', messages: 98 },
      { month: 'Feb', messages: 112 },
      { month: 'Mar', messages: 87 },
      { month: 'Apr', messages: 134 },
      { month: 'May', messages: 156 },
      { month: 'Jun', messages: 143 },
      { month: 'Jul', messages: 167 },
      { month: 'Aug', messages: 189 },
      { month: 'Sep', messages: 201 },
      { month: 'Oct', messages: 178 },
      { month: 'Nov', messages: 165 },
      { month: 'Dec', messages: 159 }
    ]
  };

  const [messages, setMessages] = useState(mockMessages);
  const [filteredMessages, setFilteredMessages] = useState(mockMessages);

  // Filter messages based on search query and filters
  useEffect(() => {
    let filtered = messages;

    // Apply search filter
    if (searchQuery?.trim()) {
      filtered = filtered?.filter(message =>
        message?.recipient?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        message?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        message?.recipient?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply other filters
    if (filters?.status && filters?.status !== 'all') {
      filtered = filtered?.filter(message => message?.status === filters?.status);
    }

    if (filters?.channel && filters?.channel !== 'all') {
      filtered = filtered?.filter(message => message?.channel === filters?.channel);
    }

    if (filters?.messageType && filters?.messageType !== 'all') {
      filtered = filtered?.filter(message => message?.type === filters?.messageType);
    }

    if (filters?.dateRange && filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'yesterday':
          filterDate?.setDate(filterDate?.getDate() - 1);
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(filterDate?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(filterDate?.getMonth() - 1);
          break;
        default:
          filterDate?.setFullYear(1970);
      }
      
      filtered = filtered?.filter(message => new Date(message.timestamp) >= filterDate);
    }

    setFilteredMessages(filtered);
  }, [searchQuery, filters, messages]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const handleLoadMore = async () => {
    setIsLoading(true);
    // Simulate loading more messages
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      setIsLoading(false);
    }, 1000);
  };

  const handleRetryMessage = async (message) => {
    // Simulate retry logic
    const updatedMessages = messages?.map(msg =>
      msg?.id === message?.id
        ? { ...msg, status: 'pending', deliveryDetails: { ...msg?.deliveryDetails, attempts: (msg?.deliveryDetails?.attempts || 0) + 1 } }
        : msg
    );
    setMessages(updatedMessages);
  };

  const handleViewDetails = (message) => {
    setSelectedMessage(message);
    setShowDetailsModal(true);
  };

  const handleContactClick = (contact) => {
    navigate('/contact-details', { state: { contactId: contact?.id } });
  };

  const handleExport = async (exportOptions) => {
    // Simulate export logic
    console.log('Exporting with options:', exportOptions);
    // In real app, this would generate and download the file
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const getStatusCounts = () => {
    const delivered = filteredMessages?.filter(m => m?.status === 'delivered')?.length;
    const pending = filteredMessages?.filter(m => m?.status === 'pending')?.length;
    const failed = filteredMessages?.filter(m => m?.status === 'failed')?.length;
    return { delivered, pending, failed };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar Navigation */}
      <BottomTabNavigation />
      {/* Main Content */}
      <div className="lg:ml-60 min-h-screen">
        {/* Navigation Breadcrumbs */}
        <NavigationBreadcrumbs />

        {/* Header */}
        <div className="sticky top-0 z-100 bg-surface border-b border-border shadow-card">
          <div className="px-4 lg:px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary">
                  Message History
                </h1>
                <p className="text-text-secondary mt-1">
                  Track and manage all your sent communications
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  iconName={showAnalytics ? "MessageSquare" : "BarChart3"}
                  iconPosition="left"
                  className="hidden md:flex"
                >
                  {showAnalytics ? "Messages" : "Analytics"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportModal(true)}
                  iconName="Download"
                  iconPosition="left"
                  className="hidden sm:flex"
                >
                  Export
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  className="hover-scale"
                >
                  <Icon name="RefreshCw" size={20} />
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative mb-4">
              <Input
                type="search"
                placeholder="Search messages, recipients, or content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
              <Icon 
                name="Search" 
                size={18} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" 
              />
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-muted rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-success">{statusCounts?.delivered}</div>
                <div className="text-xs text-text-secondary">Delivered</div>
              </div>
              <div className="bg-muted rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-warning">{statusCounts?.pending}</div>
                <div className="text-xs text-text-secondary">Pending</div>
              </div>
              <div className="bg-muted rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-error">{statusCounts?.failed}</div>
                <div className="text-xs text-text-secondary">Failed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4 lg:p-6">
          {showAnalytics ? (
            /* Analytics View */
            (<MessageAnalytics analytics={mockAnalytics} />)
          ) : (
            /* Messages View */
            (<div className="space-y-6">
              {/* Filters */}
              <MessageFilters
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={handleClearFilters}
              />
              {/* Messages List */}
              <div className="space-y-4">
                {filteredMessages?.length > 0 ? (
                  <>
                    {filteredMessages?.map((message) => (
                      <MessageCard
                        key={message?.id}
                        message={message}
                        onRetry={handleRetryMessage}
                        onViewDetails={handleViewDetails}
                        onContactClick={handleContactClick}
                      />
                    ))}

                    {/* Load More Button */}
                    {filteredMessages?.length >= 10 && (
                      <div className="flex justify-center pt-6">
                        <Button
                          variant="outline"
                          onClick={handleLoadMore}
                          loading={isLoading}
                          iconName="ChevronDown"
                          iconPosition="right"
                        >
                          {isLoading ? 'Loading...' : 'Load More Messages'}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  /* Empty State */
                  (<div className="text-center py-12">
                    <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="MessageSquare" size={32} className="text-text-secondary" />
                    </div>
                    <h3 className="text-lg font-medium text-text-primary mb-2">
                      No messages found
                    </h3>
                    <p className="text-text-secondary mb-6 max-w-md mx-auto">
                      {searchQuery || Object.keys(filters)?.length > 0
                        ? "Try adjusting your search or filters to find messages." :"You haven't sent any messages yet. Start connecting with your contacts!"}
                    </p>
                    {(!searchQuery && Object.keys(filters)?.length === 0) && (
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          variant="default"
                          onClick={() => navigate('/message-templates')}
                          iconName="MessageSquare"
                          iconPosition="left"
                        >
                          Send Message
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate('/random-contact')}
                          iconName="Shuffle"
                          iconPosition="left"
                        >
                          Random Contact
                        </Button>
                      </div>
                    )}
                  </div>)
                )}
              </div>
            </div>)
          )}
        </div>

        {/* Mobile Bottom Padding */}
        <div className="h-20 lg:hidden" />
      </div>
      {/* Mobile Bottom Navigation */}
      <BottomTabNavigation />
      {/* Modals */}
      <MessageDetailsModal
        message={selectedMessage}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        onRetry={handleRetryMessage}
        onContactClick={handleContactClick}
      />
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        totalMessages={filteredMessages?.length}
      />
    </div>
  );
};

export default MessageHistory;